/**
 * TutorialSpotlight Component (AC-669)
 *
 * Creates a spotlight effect by dimming the page except for the target element.
 * Uses SVG mask to create a transparent cutout for the target area.
 *
 * Technical Design Reference: docs/technical/TDD-AC-661-tutorial-system.md
 *
 * Features:
 * - SVG-based mask for spotlight effect
 * - Semi-transparent dark overlay
 * - Configurable padding around target
 * - Smooth transition when target changes
 * - Pointer events pass through (allows clicking target)
 * - Renders via portal for proper z-index
 * - Handles null targetRect (full dim)
 *
 * @see AC-669 - TutorialSpotlight component
 * @see AC-661 - Epic: Interactive Tutorial System
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { TutorialSpotlightProps } from '../../types/tutorial';

/**
 * Default padding around the target element (in pixels).
 */
const DEFAULT_PADDING = 8;

/**
 * Default border radius for the spotlight cutout (in pixels).
 */
const DEFAULT_BORDER_RADIUS = 8;

/**
 * TutorialSpotlight - SVG-based spotlight overlay component.
 */
const TutorialSpotlight: React.FC<TutorialSpotlightProps> = ({
  targetRect,
  padding = DEFAULT_PADDING,
}) => {
  // Calculate cutout dimensions with padding
  const cutout = targetRect
    ? {
        x: targetRect.x - padding,
        y: targetRect.y - padding,
        width: targetRect.width + 2 * padding,
        height: targetRect.height + 2 * padding,
      }
    : null;

  const spotlightContent = (
    <div
      data-testid="tutorial-spotlight"
      className="tutorial-spotlight fixed inset-0 w-full h-full"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 50,
        pointerEvents: 'none',
      }}
    >
      <svg
        width="100%"
        height="100%"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        <defs>
          <mask id="spotlight-mask">
            {/* White background makes everything visible through the mask */}
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {/* Black rectangle creates the transparent cutout */}
            {cutout && (
              <rect
                data-testid="spotlight-cutout"
                x={cutout.x}
                y={cutout.y}
                width={cutout.width}
                height={cutout.height}
                rx={DEFAULT_BORDER_RADIUS}
                fill="black"
                style={{
                  transition: 'all 0.3s ease-in-out',
                }}
              />
            )}
          </mask>
        </defs>
        {/* Semi-transparent overlay with spotlight cutout */}
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.35)"
          mask="url(#spotlight-mask)"
        />
        {/* Highlight ring around the target element */}
        {cutout && (
          <rect
            data-testid="spotlight-ring"
            x={cutout.x}
            y={cutout.y}
            width={cutout.width}
            height={cutout.height}
            rx={DEFAULT_BORDER_RADIUS}
            fill="none"
            stroke="#f97316"
            strokeWidth="2.5"
            style={{
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <animate
              attributeName="stroke-opacity"
              values="1;0.4;1"
              dur="2s"
              repeatCount="indefinite"
            />
          </rect>
        )}
      </svg>
    </div>
  );

  // Render via portal for proper z-index layering
  return createPortal(spotlightContent, document.body);
};

export default TutorialSpotlight;
